import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Načte `/api/rozvoz/davka-aktivni` (přihlášený řidič) a vrací aktuální dávku
 * + ETA + zastávky. Obnovuje se hned po `RozvozCockpitUpdated` na osobním
 * kanálu rozvozce (`rozvozce.{zamestnanecId}`, stejně jako cockpit) a po
 * návratu z pozadí (`app-resumed`); víc událostí naráz se slije do jednoho
 * dotazu (300 ms). Window focus a polling (default 30 s) zůstávají jako
 * pojistka pro výpadek WebSocketu.
 *
 * Vrací `{ davka: null }` pokud:
 *   - řidič nemá přidělenou dávku (legitimní stav)
 *   - uživatel není rozvozec
 *   - 401 (nepřihlášen)
 */
export default function useAktivniDavka({ enabled = true, pollMs = 30000, zamestnanecId = null } = {}) {
    const [data, setData] = useState(null);   // { davka, bistro, zastavky, optimum, config } | null
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchDavka = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        setError(null);
        abortRef.current?.abort?.();
        const ac = new AbortController();
        abortRef.current = ac;
        try {
            const r = await fetch('/api/rozvoz/davka-aktivni', { signal: ac.signal, credentials: 'same-origin' });
            if (!r.ok) {
                // 401/403 = nepřihlášen / neopróněno — vrať null, ne error
                if (r.status === 401 || r.status === 403) {
                    setData({ davka: null });
                    return;
                }
                throw new Error(`HTTP ${r.status}`);
            }
            const json = await r.json();
            setData(json);
        } catch (e) {
            if (e?.name === 'AbortError') return;
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [enabled]);

    useEffect(() => { fetchDavka(); }, [fetchDavka]);

    useEffect(() => {
        if (!enabled || !pollMs) return;
        const iv = setInterval(fetchDavka, pollMs);
        return () => clearInterval(iv);
    }, [fetchDavka, enabled, pollMs]);

    useEffect(() => {
        if (!enabled) return;
        const onFocus = () => fetchDavka();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [fetchDavka, enabled]);

    // Realtime: dávka se mění z kuchyně (předání), z cockpitu i z jiného
    // zařízení rozvozce — Moje směna to má vidět hned, ne až po pollu.
    useEffect(() => {
        if (!enabled) return undefined;
        let odklad;
        const brzy = () => { clearTimeout(odklad); odklad = setTimeout(fetchDavka, 300); };
        window.addEventListener('app-resumed', brzy);

        let kanal = null;
        // `window.Echo` zakládá app.jsx až ve svém efektu — chvíli se na něj čeká.
        const pripoj = () => {
            if (!zamestnanecId) return true;
            if (!window.Echo) return false;
            kanal = window.Echo.private(`rozvozce.${zamestnanecId}`);
            kanal.listen('.RozvozCockpitUpdated', brzy);
            return true;
        };
        const iv = pripoj() ? null : setInterval(() => { if (pripoj()) clearInterval(iv); }, 300);

        return () => {
            if (iv) clearInterval(iv);
            clearTimeout(odklad);
            window.removeEventListener('app-resumed', brzy);
            // stopListening, ne leave — kanál drží i cockpit rozvozce
            kanal?.stopListening('.RozvozCockpitUpdated', brzy);
        };
    }, [fetchDavka, enabled, zamestnanecId]);

    // Optimistic helper: skryje právě zavřenou (Hotovo) dávku okamžitě —
    // bez čekání na refetch, který může trvat dlouho kvůli ETA matice následující dávky.
    const markCockpitClosed = useCallback((davkaId) => {
        setData(prev => {
            if (!prev?.davka || prev.davka.id !== davkaId) return prev;
            const nadch = prev.nadchazejici_davky ?? [];
            if (nadch.length > 0) {
                const [next, ...rest] = nadch;
                return { ...prev, ...next, nadchazejici_davky: rest };
            }
            return { ...prev, davka: null, zastavky: [], nadchazejici_davky: [] };
        });
    }, []);

    // Optimistic helper: po POST /prijmout aktualizuj stav lokálně bez čekání
    // na refetch — CTA na kartě se hned přepne z „Přijmout" na „Plánovat trasu".
    const markPrijmuto = useCallback((davkaId) => {
        setData(prev => {
            if (!prev?.davka || prev.davka.id !== davkaId || prev.davka.stav !== 'pripraveno') return prev;
            return { ...prev, davka: { ...prev.davka, stav: 'potvrzena' } };
        });
    }, []);

    return {
        data,
        davka: data?.davka ?? null,
        zastavky: data?.zastavky ?? [],
        nadchazejici: data?.nadchazejici_davky ?? [],
        loading,
        error,
        refetch: fetchDavka,
        markCockpitClosed,
        markPrijmuto,
    };
}
